/**
 * Alokasi harga combo ke komponennya — **untuk LAPORAN, tak pernah untuk tagihan**.
 *
 * ### ⭐ Masalah yang diselesaikan
 * Di tagihan, induk memegang harga dan komponen nol (bentuk VTEC: 5,63 juta baris komponen,
 * 99,9999% berharga nol). Kalau itu juga yang masuk laporan, maka setiap set menu membuat laporan
 * menyatakan **dapur tidak memasak apa-apa** — padahal `department` justru ada untuk menjawab
 * pertanyaan itu.
 *
 * ⇒ Tiap komponen menyimpan `allocated_price`: **tidak ditagihkan**, hanya dilaporkan, dan
 * **Σ-nya EKSAK sama** dengan harga induk.
 *
 * ### ⚠️ Kenapa "eksak" itu bukan basa-basi
 * Pembagian proporsional biasa akan meleset beberapa rupiah karena pembulatan. Selisih itu kecil
 * per transaksi dan **menumpuk**: 5,6 juta baris komponen × Rp1 = Rp5,6 juta yang tak bisa
 * dijelaskan siapa pun. Karena itu memakai `allocateMoney` (sisa terbesar) — sama seperti mesin
 * pajak (§8), bukan pembagian yang "cukup dekat".
 *
 * ⛔ Di `@osbpos/domain`, bukan di `portal-api`, karena outlet wajib bisa menghitungnya saat
 * internet putus (K3).
 */
import { allocateMoney, money, moneyToWire, sumMoney } from '@osbpos/contract';
/**
 * Membagi `parentPrice` ke komponen menurut bobotnya.
 *
 * @param parentPrice harga induk **setelah** upcharge pilihan premium — itulah yang benar-benar
 *   ditagihkan, jadi itu pula yang harus habis dibagi.
 *
 * ⚠️ **Bobot nol seluruhnya ⇒ dibagi RATA**, bukan dilempar. Alasannya P8 dibaca dengan benar:
 * yang dilarang adalah menerbitkan dokumen turunan saat masukannya **tak ada**; di sini masukannya
 * ada (harga induk nyata), yang tak ada hanyalah harga à la carte komponen — dan itu wajar untuk
 * item yang memang hanya dijual di dalam paket. Membagi rata tetap menjaga Σ eksak.
 *
 * ⚠️ Sebagian bobot nol ⇒ komponen itu memang dapat **0**. Itu jawaban yang benar: item pelengkap
 * gratis (kerupuk, acar) tak boleh menyerap pendapatan dari lauk utamanya.
 */
export function allocateComboComponentPrices(parentPrice, components) {
    if (components.length === 0)
        return [];
    const weights = components.map((k) => k.weight);
    const total = sumMoney(weights);
    const bagian = isNol(total)
        ? allocateMoney(parentPrice, components.map(() => ONE)) // semua bobot nol ⇒ rata
        : allocateMoney(parentPrice, weights);
    return components.map((k, i) => ({
        productCode: k.productCode,
        allocatedPrice: bagian[i],
    }));
}
function isNol(m) {
    return /^0(\.0+)?$/.test(moneyToWire(m));
}
const ONE = money('1');
//# sourceMappingURL=combo-allocation.js.map